<template>
  <b-container fluid>
    <loader :watch="['project', 'projectSubstations']">
      <div class="form-view col-xl-8">
        <div :key="errorsCounter">
          <b-alert
            v-for="input in inputsErrors"
            :key="input.code"
            dismissible
            show
            variant="danger"
          >
            <span> {{ input.details }} </span>
          </b-alert>
        </div>
        <b-row>
          <b-col>
            <h2>{{ title }}</h2>
          </b-col>
          <b-col v-if="projectCode" class="text-right">
            <b-button variant="rounded" @click="removeProjectModal = !removeProjectModal">
              Delete project
              <Icon name="delete" />
            </b-button>
            <b-modal
              ref="modal"
              v-model="removeProjectModal"
              ok-title="Delete"
              cancel-title="Cancel"
              ok-variant="rounded btn-delete"
              cancel-variant="link"
              class="text-left"
              @ok="removeProject"
            >
              <p class="lead">Are you sure you want to delete this project?</p>
              You won't be able to undo your action.
            </b-modal>
          </b-col>
        </b-row>
        <b-form ref="form" novalidate @submit.prevent="submit">
          <b-form-group label="Name">
            <b-form-input v-model="projectForm.name" />
          </b-form-group>
          <b-form-group
            v-if="!(project && project.code) && $route.name === 'newProject'"
            label="Code"
            :class="{ 'form-group--error': $v.projectForm.code.$error }"
          >
            <b-form-input
              v-model="projectForm.code"
              name="project-code"
              @input="handleInputField('code')"
            />
            <ValidationMessage
              v-if="$v.projectForm.code.$invalid"
              :message="returnErrorMessage($v.projectForm.code)"
              class="form-group__message--show"
            />
          </b-form-group>
          <b-form-group label="Time zone">
            <v-select
              v-model="projectForm.timeZone"
              :options="timezonesKeys"
              class="custom-select"
              :clearable="false"
              :disabled="$route.name === 'editProject'"
            />
          </b-form-group>
          <b-form-group v-if="projectCode" label="" class="form-group--checkbox">
            <b-form-checkbox v-model="projectForm.telegramChannelActive">
              Telegram channel
            </b-form-checkbox>
          </b-form-group>
          <b-form-group
            v-if="$route.name === 'editProject'"
            label="Telegram API access token"
            :class="{
              'form-group--error':
                !!$v.projectForm.telegramAuthToken && $v.projectForm.telegramAuthToken.$error
            }"
          >
            <b-form-input
              v-model.trim="projectForm.telegramAuthToken"
              name="telegramAuthToken"
              :disabled="!projectForm.telegramChannelActive"
              @input="handleInputField('telegramAuthToken')"
            />
            <ValidationMessage
              v-show="showErrorMessage('telegramAuthToken')"
              :message="returnErrorMessage($v.projectForm.telegramAuthToken)"
              class="form-group__message--show"
            />
          </b-form-group>
          <b-form-group
            v-if="$route.name === 'editProject'"
            label="Telegram channel identifier"
            :class="{
              'form-group--error':
                !!$v.projectForm.telegramChannelId && $v.projectForm.telegramChannelId.$error
            }"
          >
            <b-form-input
              v-model="projectForm.telegramChannelId"
              name="telegramChannelId"
              :disabled="!projectForm.telegramChannelActive"
              @input="handleInputField('telegramChannelId')"
            />
            <ValidationMessage
              v-show="showErrorMessage('telegramChannelId')"
              :message="returnErrorMessage($v.projectForm.telegramChannelId)"
              class="form-group__message--show"
            />
          </b-form-group>
          <b-form-group class="form-group__buttons">
            <router-link
              class="btn btn-link font-weight-bold"
              variant="link"
              :to="{ name: 'projects' }"
            >
              Cancel
            </router-link>
            <b-button type="submit" :disabled="$v.projectForm.$invalid" variant="rounded">
              Save
              <Icon name="save" />
            </b-button>
          </b-form-group>
        </b-form>
      </div>
    </loader>
  </b-container>
</template>

<script>
import {
  required,
  alphanumWithUnderscore,
  checkForWhiteSpace
} from '@/lib/vuelidate-custom-validators';
import { timezonesList } from '@/lib/timezones';

const ERRORS = {
  checkForWhiteSpace: 'Remove whitespaces from input',
  required: 'This field is required',
  alphanumWithUnderscore: 'Invalid signs used'
};
const FIELDS_TO_VALIDATE = {
  newProject: {
    projectForm: {
      code: {
        required,
        checkForWhiteSpace,
        alphanumWithUnderscore
      }
    }
  },
  editProject: {
    projectForm: {
      telegramAuthToken: {
        required,
        checkForWhiteSpace
      },
      telegramChannelId: {
        required,
        checkForWhiteSpace
      }
    }
  },
  noTelegram: {
    projectForm: {
      telegramAuthToken: {},
      telegramChannelId: {}
    }
  }
};
const DEFAULT_TIMEZONE = 'London';

export default {
  name: 'ProjectForm',
  props: {
    title: String,
    mainError: String,
    inputsErrors: Array,
    projectCode: String,
    project: {
      name: String,
      code: String,
      timeZone: String,
      telegramChannelActive: Boolean,
      telegramAuthToken: String,
      telegramChannelId: String
    }
  },
  data() {
    return {
      errorsCounter: 0,
      projectForm: {
        name: '',
        code: '',
        timeZone: DEFAULT_TIMEZONE,
        telegramAuthToken: '',
        telegramChannelId: ''
      },
      removeProjectModal: false,
      timezonesKeys: Object.keys(timezonesList)
    };
  },
  validations() {
    const { name } = this.$route;

    if (!this.projectForm.telegramChannelActive && name === 'editProject') {
      return FIELDS_TO_VALIDATE.noTelegram;
    }

    return FIELDS_TO_VALIDATE[name];
  },
  watch: {
    project(project) {
      this.updateProjectData(project);
    },
    inputsErrors() {
      this.errorsCounter += 1;
    }
  },
  mounted() {
    this.updateProjectData(this.project);
    this.$v.$touch();
  },
  methods: {
    handleInputField(fieldName) {
      const input = this.$v.projectForm[fieldName];

      this.removeResponseErrors(fieldName);
      if (input) input.$touch();
    },
    removeResponseErrors() {
      if (this.inputsErrors.length > 0) {
        this.responseErrors = [];
        this.$v.$reset();
      }

      if (this.error) {
        this.error = '';
      }
    },
    showErrorMessage(typeOfField) {
      return (
        this.$v.projectForm[typeOfField].$invalid &&
        this.$v.projectForm[typeOfField].$dirty &&
        this.projectForm.telegramChannelActive
      );
    },
    updateProjectData(project) {
      if (project) {
        this.projectForm = project;
      }
    },
    returnErrorMessage(errorType) {
      const { $invalid, $model, $dirty } = errorType;

      if ($invalid && $dirty) {
        const errorsList = Object.keys(errorType).filter(
          error => !error.startsWith('$') && error !== 'required'
        );

        if ($model && errorsList.length > 0) {
          const errorMessage = errorsList.find(errorName => !errorType[errorName]);

          return ERRORS[errorMessage];
        }
        return ERRORS.required;
      }
      return '';
    },
    submit() {
      this.$emit('submit', {
        ...this.projectForm
      });
    },
    removeProject() {
      this.$store.dispatch('projects/removeProject', this.projectCode).then(() => {
        this.$router.push({
          name: 'projects'
        });
      });
    }
  }
};
</script>
